import React, { useState } from 'react';
import {
  CheckCircle2,
  MapPin,
  Download,
  RotateCcw,
  Compass,
  Folder,
  Sparkles,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { IngestionCompletionSummary } from '../../types/ingestion';

export interface IngestionSummaryProps {
  summary: IngestionCompletionSummary;
  onOpenMap: () => void;
  onExportPackage?: () => void;
  onReset: () => void;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${(bytes / Math.pow(k, i)).toFixed(1)} ${sizes[i]}`;
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  if (m === 0) return `${s} secondi`;
  return `${m} min ${s} sec`;
}

export const IngestionSummary: React.FC<IngestionSummaryProps> = ({
  summary,
  onOpenMap,
  onExportPackage,
  onReset,
}) => {
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  const {
    trip,
    processedPhotos,
    failedPhotos = 0,
    durationSeconds,
    averageThroughput,
    storage,
    matching,
    categories,
    reconciledTimezone,
    timezoneTier,
    errors = [],
  } = summary;

  const isSuccess = processedPhotos > 0;

  return (
    <div className="w-full max-w-3xl mx-auto flex flex-col gap-6 p-4 sm:p-6 bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl">
      {/* Celebration or Alert Header */}
      <div className="flex items-center gap-4 border-b border-slate-800 pb-5">
        <div
          className={`w-14 h-14 rounded-2xl border flex items-center justify-center shadow-lg ${
            isSuccess
              ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400 shadow-emerald-500/10'
              : 'bg-amber-500/10 border-amber-500/30 text-amber-400 shadow-amber-500/10'
          }`}
        >
          {isSuccess ? <CheckCircle2 className="w-8 h-8" /> : <AlertTriangle className="w-8 h-8" />}
        </div>
        <div>
          <h2 className="text-xl font-bold text-slate-100 flex items-center gap-2">
            <span>
              {isSuccess
                ? failedPhotos > 0
                  ? 'Elaborazione Completata con Avvisi'
                  : 'Ingestione e Compressione Completate!'
                : 'Nessuna Foto Elaborata'}
            </span>
            {isSuccess && <Sparkles className="w-4 h-4 text-amber-400" />}
          </h2>
          <p className="text-xs sm:text-sm text-slate-400 mt-0.5">
            {isSuccess
              ? `${processedPhotos} foto elaborate in ${formatDuration(durationSeconds)} (media di ${averageThroughput.toFixed(1)} foto/s)${failedPhotos > 0 ? `, ${failedPhotos} non riuscite.` : '.'}`
              : 'Nessuna foto è stata salvata nel database locale. Controlla i dettagli degli errori qui sotto.'}
          </p>
        </div>
      </div>

      {/* Primary KPI Grid (4 Cards) */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* KPI 1: Photos */}
        <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3 flex flex-col justify-between">
          <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Foto Elaborate</div>
          <div className="text-xl font-bold text-slate-100 my-1">{processedPhotos}</div>
          <div className="text-[10px] text-emerald-400 font-medium">100% completate</div>
        </div>

        {/* KPI 2: Storage Saved */}
        <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3 flex flex-col justify-between">
          <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Spazio WebP</div>
          <div className="text-xl font-bold text-slate-100 my-1">{formatBytes(storage.totalCompressedBytes)}</div>
          <div className="text-[10px] text-emerald-400 font-medium">-{storage.compressionRatioPercent}% risparmiato</div>
        </div>

        {/* KPI 3: Geotag Rate */}
        <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3 flex flex-col justify-between">
          <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Geolocalizzazione</div>
          <div className="text-xl font-bold text-slate-100 my-1">{matching.matchRatePercentage.toFixed(1)}%</div>
          <div className="text-[10px] text-brand-400 font-medium">{matching.totalGeotagged} su mappa</div>
        </div>

        {/* KPI 4: Timezone */}
        <div className="bg-slate-800/60 border border-slate-700/60 rounded-xl p-3 flex flex-col justify-between">
          <div className="text-[11px] font-medium text-slate-400 uppercase tracking-wider">Fuso Orario</div>
          <div className="text-sm font-bold text-slate-100 my-1 truncate" title={reconciledTimezone}>
            {reconciledTimezone}
          </div>
          <div className="text-[10px] text-slate-400 truncate">{timezoneTier}</div>
        </div>
      </div>

      {/* GPS Matching Distribution Panel */}
      <div className="bg-slate-800/40 border border-slate-800 rounded-xl p-4 flex flex-col gap-3">
        <div className="flex items-center justify-between text-xs font-semibold text-slate-300">
          <div className="flex items-center gap-1.5">
            <Compass className="w-4 h-4 text-brand-400" />
            <span>Distribuzione Correlazione GPS Polarsteps</span>
          </div>
          <span className="text-slate-400 font-normal">
            Traccia: {trip.locations.length} punti GPS • {trip.all_steps.length} tappe
          </span>
        </div>

        {/* Stacked Percentage Bar */}
        <div className="w-full h-3.5 bg-slate-950 rounded-full overflow-hidden flex border border-slate-700/50">
          <div
            className="bg-emerald-500 transition-all"
            style={{ width: `${processedPhotos > 0 ? (matching.exact / processedPhotos) * 100 : 0}%` }}
            title={`Esatte (Satellite / EXIF): ${matching.exact}`}
          />
          <div
            className="bg-sky-500 transition-all"
            style={{ width: `${processedPhotos > 0 ? (matching.interpolated / processedPhotos) * 100 : 0}%` }}
            title={`Interpolazione Polarsteps: ${matching.interpolated}`}
          />
          <div
            className="bg-amber-500 transition-all"
            style={{ width: `${processedPhotos > 0 ? (matching.snapFallback / processedPhotos) * 100 : 0}%` }}
            title={`Snappate (< 10 min): ${matching.snapFallback}`}
          />
          <div
            className="bg-purple-500 transition-all"
            style={{ width: `${processedPhotos > 0 ? (matching.stepFallback / processedPhotos) * 100 : 0}%` }}
            title={`Tappa Check-in Fallback: ${matching.stepFallback}`}
          />
          <div
            className="bg-rose-500 transition-all"
            style={{ width: `${processedPhotos > 0 ? (matching.unmatched / processedPhotos) * 100 : 0}%` }}
            title={`Non geolocalizzate: ${matching.unmatched}`}
          />
        </div>

        {/* Distribution Legend */}
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 pt-1 text-[11px]">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 flex-shrink-0" />
            <span className="text-slate-300 font-medium">Esatte:</span>
            <span className="text-slate-400">{matching.exact}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-sky-500 flex-shrink-0" />
            <span className="text-slate-300 font-medium">Interpolate:</span>
            <span className="text-slate-400">{matching.interpolated}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-amber-500 flex-shrink-0" />
            <span className="text-slate-300 font-medium">Snappate:</span>
            <span className="text-slate-400">{matching.snapFallback}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-purple-500 flex-shrink-0" />
            <span className="text-slate-300 font-medium">Tappa:</span>
            <span className="text-slate-400">{matching.stepFallback}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-rose-500 flex-shrink-0" />
            <span className="text-slate-300 font-medium">Non geoloc:</span>
            <span className="text-slate-400">{matching.unmatched}</span>
          </div>
        </div>
      </div>

      {/* Category Folders Breakdown Table */}
      <div className="flex flex-col gap-2">
        <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-300">
          <Folder className="w-4 h-4 text-brand-400" />
          <span>Suddivisione per Cartelle / Categorie Riconosciute</span>
        </div>

        <div className="border border-slate-800 rounded-xl overflow-hidden">
          <table className="w-full text-left text-xs text-slate-300">
            <thead className="bg-slate-800/80 text-[10px] font-semibold text-slate-400 uppercase tracking-wider border-b border-slate-800">
              <tr>
                <th className="py-2.5 px-3">Categoria</th>
                <th className="py-2.5 px-3 text-right">Foto</th>
                <th className="py-2.5 px-3 text-right">Su Mappa</th>
                <th className="py-2.5 px-3 text-right">Spazio WebP</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/60 font-mono">
              {categories.map((cat) => (
                <tr key={cat.category} className="hover:bg-slate-800/30 transition">
                  <td className="py-2.5 px-3 font-sans font-medium text-slate-200 flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-brand-400" />
                    {cat.category}
                  </td>
                  <td className="py-2.5 px-3 text-right">{cat.photoCount}</td>
                  <td className="py-2.5 px-3 text-right text-emerald-400">
                    {cat.geotaggedCount} ({cat.geotaggedPercent.toFixed(0)}%)
                  </td>
                  <td className="py-2.5 px-3 text-right text-slate-400">{formatBytes(cat.totalStorageBytes)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Error Details Panel if any */}
      {errors.length > 0 && (
        <div className="bg-red-950/30 border border-red-800/60 rounded-xl p-4 flex flex-col gap-2">
          <button
            type="button"
            onClick={() => setShowErrorDetails((prev) => !prev)}
            className="flex items-center justify-between text-xs font-semibold text-red-300 hover:text-red-200 transition"
          >
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span>Avvisi / Errori di Elaborazione ({errors.length})</span>
            </div>
            {showErrorDetails ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>

          {showErrorDetails && (
            <div className="mt-2 max-h-48 overflow-y-auto space-y-1.5 text-xs text-red-300/90 font-mono bg-red-950/40 p-2 rounded-lg border border-red-900/50">
              {errors.map((err, idx) => (
                <div key={idx} className="flex flex-col sm:flex-row sm:gap-2">
                  <span className="font-semibold text-red-200">{err.fileName}:</span>
                  <span className="text-red-400">{err.reason}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Primary Actions Bar */}
      <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-800">
        <button
          type="button"
          onClick={onReset}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-xl text-xs font-medium transition cursor-pointer"
        >
          <RotateCcw className="w-4 h-4" />
          Elabora Altre Foto
        </button>

        <div className="flex items-center gap-3 w-full sm:w-auto">
          {onExportPackage && (
            <button
              type="button"
              disabled={!isSuccess}
              onClick={onExportPackage}
              className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium transition ${
                isSuccess
                  ? 'bg-slate-700 hover:bg-slate-600 text-slate-100 cursor-pointer'
                  : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
              }`}
            >
              <Download className="w-4 h-4" />
              Esporta ZIP
            </button>
          )}

          <button
            type="button"
            disabled={!isSuccess}
            onClick={onOpenMap}
            className={`flex-1 sm:flex-initial flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl text-xs sm:text-sm font-semibold transition shadow-lg ${
              isSuccess
                ? 'bg-gradient-to-r from-brand-600 to-brand-500 hover:from-brand-500 hover:to-brand-400 text-white shadow-brand-500/25 cursor-pointer'
                : 'bg-slate-800 text-slate-500 cursor-not-allowed border border-slate-700'
            }`}
          >
            <MapPin className="w-4 h-4" />
            Esplora sulla Mappa
          </button>
        </div>
      </div>
    </div>
  );
};
